import React, { useState, useEffect } from 'react';
import { lookupsApi, backtestApi } from '../../services/api';
import { Instrument, Resolution } from '../../types';
import Dropdown from '../../components/common/Dropdown';
import Button from '../../components/common/Button';
import ResultModal from '../../components/common/ResultModal';
import './FetchData.css';

const FetchData: React.FC = () => {
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [resolutions, setResolutions] = useState<Resolution[]>([]);

  const [fetchInstrument, setFetchInstrument] = useState('');
  const [fetchResolution, setFetchResolution] = useState('');
  const [fetchFromDate, setFetchFromDate] = useState('2024-01-01');
  const [fetchToDate, setFetchToDate] = useState('2025-01-01');
  const [fetchLoading, setFetchLoading] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalVariant, setModalVariant] = useState<'success' | 'error' | 'warning'>('success');
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState<any>(null);

  // Candles table state
  const [candles, setCandles] = useState<any[]>([]);
  const [candlesLoading, setCandlesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const candlesPerPage = 15;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (fetchInstrument && fetchResolution) {
      loadCandles();
    }
  }, [fetchInstrument, fetchResolution]);

  const loadData = async () => {
    try {
      const [insts, ress] = await Promise.all([
        lookupsApi.getInstruments(),
        lookupsApi.getResolutions()
      ]);
      setInstruments(insts);
      setResolutions(ress);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadCandles = async () => {
    if (!fetchInstrument || !fetchResolution) return;

    setCandlesLoading(true);
    try {
      const result = await backtestApi.getRecentCandles(fetchInstrument, fetchResolution);
      if (result.success && result.data) {
        setCandles(result.data);
        setCurrentPage(1); // Reset to first page
      } else {
        setCandles([]);
        setModalTitle('Failed to Load Candles');
        setModalVariant('error');
        setModalContent(
          <div className="error-message">
            {result.message || 'Failed to load recent candles'}
          </div>
        );
        setShowModal(true);
      }
    } catch (error: any) {
      console.error('Error loading candles:', error);
      setCandles([]);

      let errorMessage = 'Failed to load recent candles.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        errorMessage = typeof error.response.data === 'string'
          ? error.response.data
          : 'Failed to load candles. Check console for details.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setModalTitle('Failed to Load Candles');
      setModalVariant('error');
      setModalContent(<div className="error-message">{errorMessage}</div>);
      setShowModal(true);
    } finally {
      setCandlesLoading(false);
    }
  };

  const handleFetchData = async () => {
    if (!fetchInstrument || !fetchResolution) {
      alert('Please select instrument and resolution');
      return;
    }

    setFetchLoading(true);

    try {
      const result = await backtestApi.fetchHistoricalData({
        userId: 1,
        epic: fetchInstrument,
        resolution: fetchResolution,
        fromDate: `${fetchFromDate}T00:00:00`,
        toDate: `${fetchToDate}T00:00:00`
      });

      console.log('Fetch data result:', result);

      if (result.success) {
        const candlesStored = result.data?.candlesStored || result.data?.candles_stored || 0;

        const content = (
          <div className="fetch-data-summary">
            <div className="summary-cards-grid">
              <div className="summary-card">
                <span className="summary-label">Candles Fetched</span>
                <span className="summary-value success-text">{candlesStored}</span>
              </div>
              <div className="summary-card">
                <span className="summary-label">Instrument</span>
                <span className="summary-value">{fetchInstrument}</span>
              </div>
              <div className="summary-card">
                <span className="summary-label">Resolution</span>
                <span className="summary-value">{fetchResolution}</span>
              </div>
            </div>
            {result.message && (
              <div style={{ marginTop: '1rem', color: '#636e72', textAlign: 'center' }}>
                {result.message}
              </div>
            )}
          </div>
        );

        setModalTitle('Data Fetched Successfully');
        setModalVariant('success');
        setModalContent(content);
        setShowModal(true);

        // Reload candles table after fetching new data
        loadCandles();
      } else {
        setModalTitle('Fetch Failed');
        setModalVariant('error');
        setModalContent(
          <div className="error-message">
            {result.message || 'Failed to fetch data'}
          </div>
        );
        setShowModal(true);
      }
    } catch (error: any) {
      console.error('Error fetching historical data:', error);

      let errorMessage = 'Failed to fetch data.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data) {
        errorMessage = typeof error.response.data === 'string'
          ? error.response.data
          : 'Failed to fetch data. Check console for details.';
      } else if (error.message) {
        errorMessage = error.message;
      }

      setModalTitle('Fetch Failed');
      setModalVariant('error');
      setModalContent(<div className="error-message">{errorMessage}</div>);
      setShowModal(true);
    } finally {
      setFetchLoading(false);
    }
  };

  return (
    <div className="fetch-data-page">
      <div className="page-header">
        <h2>Fetch Historical Data</h2>
        <p className="page-description">Download historical market data from IG Markets for backtesting</p>
      </div>

      <div className="fetch-data-card">
        <div className="form-grid">
          <Dropdown
            label="Instrument"
            value={fetchInstrument}
            onChange={setFetchInstrument}
            options={instruments.map(i => ({
              value: i.epic,
              label: `${i.name} (${i.epic})`
            }))}
            placeholder="Select Instrument"
          />

          <Dropdown
            label="Resolution"
            value={fetchResolution}
            onChange={setFetchResolution}
            options={resolutions.map(r => ({
              value: r.value,
              label: r.label
            }))}
            placeholder="Select Resolution"
          />

          <div className="date-input-container">
            <label>From Date</label>
            <input
              type="date"
              value={fetchFromDate}
              onChange={(e) => setFetchFromDate(e.target.value)}
              className="date-input"
            />
          </div>

          <div className="date-input-container">
            <label>To Date</label>
            <input
              type="date"
              value={fetchToDate}
              onChange={(e) => setFetchToDate(e.target.value)}
              min={fetchFromDate}
              className="date-input"
            />
          </div>
        </div>

        <Button
          onClick={handleFetchData}
          loading={fetchLoading}
          disabled={fetchLoading || !fetchInstrument || !fetchResolution}
          variant="secondary"
        >
          Fetch Data
        </Button>
      </div>

      {candles.length > 0 && (
        <div className="candles-table-card">
          <h3>Recent Candles ({candles.length} candles)</h3>
          {candlesLoading ? (
            <div className="loading-text">Loading candles...</div>
          ) : (
            <>
              <table className="candles-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Open</th>
                    <th>High</th>
                    <th>Low</th>
                    <th>Close</th>
                    <th>Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {candles
                    .slice((currentPage - 1) * candlesPerPage, currentPage * candlesPerPage)
                    .map((candle, index) => (
                      <tr key={candle.candle_id || index}>
                        <td>{(() => {
                          // Ensure the timestamp is treated as UTC by appending 'Z' if not present
                          const timestamp = candle.timestamp.endsWith('Z') ? candle.timestamp : candle.timestamp + 'Z';
                          return new Date(timestamp).toLocaleString('en-US', {
                            timeZone: 'America/Mexico_City',
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: false
                          });
                        })()}</td>
                        <td>${candle.open_price?.toFixed(4) || '0.0000'}</td>
                        <td>${candle.high_price?.toFixed(4) || '0.0000'}</td>
                        <td>${candle.low_price?.toFixed(4) || '0.0000'}</td>
                        <td>${candle.close_price?.toFixed(4) || '0.0000'}</td>
                        <td>{candle.volume || 0}</td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {Math.ceil(candles.length / candlesPerPage) > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="page-button"
                  >
                    Previous
                  </button>

                  {(() => {
                    const totalPages = Math.ceil(candles.length / candlesPerPage);
                    const maxPagesToShow = 7;
                    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

                    // Adjust start if we're near the end
                    if (endPage - startPage < maxPagesToShow - 1) {
                      startPage = Math.max(1, endPage - maxPagesToShow + 1);
                    }

                    const pages = [];

                    // First page
                    if (startPage > 1) {
                      pages.push(
                        <button
                          key={1}
                          onClick={() => setCurrentPage(1)}
                          className="page-button"
                        >
                          1
                        </button>
                      );
                      if (startPage > 2) {
                        pages.push(<span key="ellipsis-start" className="pagination-ellipsis">...</span>);
                      }
                    }

                    // Page numbers
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`page-button ${currentPage === i ? 'active' : ''}`}
                        >
                          {i}
                        </button>
                      );
                    }

                    // Last page
                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(<span key="ellipsis-end" className="pagination-ellipsis">...</span>);
                      }
                      pages.push(
                        <button
                          key={totalPages}
                          onClick={() => setCurrentPage(totalPages)}
                          className="page-button"
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}

                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === Math.ceil(candles.length / candlesPerPage)}
                    className="page-button"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <ResultModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={modalTitle}
        variant={modalVariant}
      >
        {modalContent}
      </ResultModal>
    </div>
  );
};

export default FetchData;
